// RiskMeter component - main entry point
import React from 'react';
import { RiskMeterProps, RiskMeterPresets } from './types/riskMeterTypes';
import { RiskMeterVariant, getVariantSettings } from './types/riskMeterVariants';
import { useRiskMeterController } from './hooks/useRiskMeterController';
import RiskMeterView from './components/RiskMeterView';

export { RiskMeterPresets };
export type { RiskMeterProps };

// Loading, error and empty all fill the meter's own size, so nothing shifts once data arrives.
// The icon has no room for words and shows a single character instead.
function RiskMeterPlaceholder({ variant, height, width, color, text, iconText }: {
    variant: RiskMeterVariant;
    height?: number;
    width?: number;
    color: string;
    text: string;
    iconText: string;
}) {
    const settings = getVariantSettings(variant);

    return (
        <div style={{
            height: height ?? settings.height,
            width: width ?? settings.width,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color
        }}>
            {settings.supportsText ? text : iconText}
        </div>
    );
}

export default function RiskMeter(props: RiskMeterProps) {
    const {
        data,
        isLoading,
        error,
        screenshotTitle,
        containerRef,
        exportAsPng,
        variant = 'full',
        height,
        width,
        showLabels = true,
        showConnectors = true,
        showTooltip = true,
        showCenterCircle = true,
        showBackground = false,
        showTable = true,
        showLegend = true,
        grantId,
        financial,
        reload
    } = useRiskMeterController(props);

    if (isLoading) {
        return <RiskMeterPlaceholder variant={variant} height={height} width={width} color="#999" text="Loading..." iconText="..." />;
    }

    if (error) {
        return <RiskMeterPlaceholder variant={variant} height={height} width={width} color="#c00" text={error} iconText="!" />;
    }

    if (!data) {
        return <RiskMeterPlaceholder variant={variant} height={height} width={width} color="#999" text="No data" iconText="-" />;
    }

    return (
        <RiskMeterView
            data={data}
            variant={variant}
            height={height}
            width={width}
            showLabels={showLabels}
            showConnectors={showConnectors}
            showTooltip={showTooltip}
            showCenterCircle={showCenterCircle}
            showBackground={showBackground}
            showTable={showTable}
            showLegend={showLegend}
            screenshotTitle={screenshotTitle}
            containerRef={containerRef}
            exportAsPng={exportAsPng}
            grantId={grantId}
            financial={financial}
            reload={reload}
        />
    );
}
